import classNames from "classnames";
import { ReactNode } from "react";
import { Slice } from "../../types";
import styles from "./style.module.scss";

const RADIUS = 40;

interface DonutChartProps {
  slices: Slice[];
  /** Rendered px size. The SVG itself is a 0 0 100 100 viewBox, so it scales. */
  size?: number;
  /** Stroke width in viewBox units. 14 matches the design, 10 for the plan ring. */
  thickness?: number;
  /** Colour behind the slices. Pass null for a fully consumed ring. */
  trackColor?: string | null;
  /** Rounds the arc ends. The design only does this on the funnel rings. */
  rounded?: boolean;
  /** Absolute-positioned content in the middle of the ring. */
  center?: ReactNode;
  className?: string;
}

/**
 * Hand-rolled SVG donut, matching the design file exactly.
 *
 * Each slice is a full circle whose dash array exposes only its own arc, and
 * whose dash offset pushes it past every slice before it. Nothing is animated
 * on mount so a skeleton can hand over without a visible jump.
 */
function DonutChart({
  slices,
  size = 150,
  thickness = 14,
  trackColor = "#f1eef2",
  rounded,
  center,
  className,
}: DonutChartProps) {
  const circumference = 2 * Math.PI * RADIUS;
  let consumed = 0;

  const arcs = slices.map((slice) => {
    // Clamp so a zero-share slice still paints a hairline instead of vanishing.
    const share = Math.max(0, Math.min(100, slice.share));
    const length = (share / 100) * circumference;
    const offset = -consumed;
    consumed += length;

    return {
      ...slice,
      dasharray: `${length.toFixed(2)} ${(circumference - length).toFixed(2)}`,
      dashoffset: offset.toFixed(2),
    };
  });

  return (
    <div
      className={classNames(styles.donut, className)}
      style={{ width: size, height: size, flexBasis: size }}
    >
      <svg viewBox="0 0 100 100" className={styles.svg}>
        {trackColor && (
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke={trackColor}
            strokeWidth={thickness}
          />
        )}
        {arcs.map((arc, index) => (
          <circle
            key={`${arc.label}-${index}`}
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke={arc.color}
            strokeWidth={thickness}
            strokeDasharray={arc.dasharray}
            strokeDashoffset={arc.dashoffset}
            strokeLinecap={rounded ? "round" : "butt"}
          >
            <title>{`${arc.label}: ${arc.share}%`}</title>
          </circle>
        ))}
      </svg>
      {center && <div className={styles.center}>{center}</div>}
    </div>
  );
}

export default DonutChart;
